import { useState } from 'react';
import { CheckCircle2, AlertCircle, CircleDashed, ChevronDown } from 'lucide-react';
import type { Finding, RepositoryAnalysis } from '../types/analysis.types';

interface CategoryBreakdownProps {
  data: RepositoryAnalysis;
}

type FindingGroup = 'detected' | 'gap' | 'issue';

function classifyFinding(f: Finding): FindingGroup {
  if (f.kind === 'positive') return 'detected';
  if (f.kind === 'issue') return 'issue';
  return 'gap';
}

const SEVERITY_RANK: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };

function bySeverity(items: Finding[]): Finding[] {
  return [...items].sort(
    (a, b) => (SEVERITY_RANK[a.severity ?? ''] ?? 4) - (SEVERITY_RANK[b.severity ?? ''] ?? 4),
  );
}

// collapsed-row dots count problems by severity (matching the chips), so
// red/orange here always corresponds to an entry in the Critical Gaps panel
const SEVERITY_DOTS: { severity: string; dotClass: string }[] = [
  { severity: 'critical', dotClass: 'bg-red-500' },
  { severity: 'high', dotClass: 'bg-orange-500' },
  { severity: 'medium', dotClass: 'bg-yellow-500' },
  { severity: 'low', dotClass: 'bg-slate-400' },
];

function severityCounts(
  findings: Finding[],
): { severity: string; count: number; dotClass: string }[] {
  const problems = findings.filter((f) => classifyFinding(f) !== 'detected');
  return SEVERITY_DOTS.map(({ severity, dotClass }) => ({
    severity,
    dotClass,
    count: problems.filter((f) => (f.severity ?? 'medium') === severity).length,
  })).filter((b) => b.count > 0);
}

function severityIconClass(severity: string): string {
  if (severity === 'critical') return 'text-red-500';
  if (severity === 'high') return 'text-orange-500';
  if (severity === 'medium') return 'text-yellow-500';
  return 'text-slate-400';
}

function maxSeverity(items: Finding[]): string {
  const order = ['critical', 'high', 'medium', 'low'];
  for (const sev of order) {
    if (items.some((f) => (f.severity ?? 'medium') === sev)) return sev;
  }
  return 'low';
}

function severityBandClasses(severity: string): { col: string; icon: string; section: string } {
  if (severity === 'critical' || severity === 'high') {
    return {
      col: 'bg-red-100/60 dark:bg-red-900/30',
      icon: severity === 'critical' ? 'text-red-500' : 'text-orange-500',
      section: 'text-red-700 dark:text-red-400',
    };
  }
  if (severity === 'medium') {
    return {
      col: 'bg-yellow-100/60 dark:bg-yellow-900/30',
      icon: 'text-yellow-500',
      section: 'text-yellow-700 dark:text-yellow-400',
    };
  }
  return {
    col: 'bg-slate-100/60 dark:bg-slate-700/30',
    icon: 'text-slate-400',
    section: 'text-slate-600 dark:text-slate-400',
  };
}

// same chip styling as the Critical Gaps panel (InsightsGrid) so severity
// reads identically in both sections
function severityChipClass(severity: string): string {
  if (severity === 'critical')
    return 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300';
  if (severity === 'high')
    return 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300';
  if (severity === 'medium')
    return 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300';
  return 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300';
}

const groupConfig: Record<
  FindingGroup,
  {
    label: string;
    icon: typeof CheckCircle2;
    iconClass: string;
    sectionClass: string;
    rowClass: string;
    dotClass: string;
    colClass: string;
  }
> = {
  detected: {
    label: 'In Place',
    icon: CheckCircle2,
    iconClass: 'text-emerald-500',
    sectionClass: 'text-emerald-700 dark:text-emerald-400',
    rowClass: 'bg-emerald-50/40 dark:bg-emerald-900/10',
    dotClass: 'bg-emerald-500',
    colClass: 'bg-emerald-100/60 dark:bg-emerald-900/30',
  },
  gap: {
    label: 'Missing',
    icon: CircleDashed,
    iconClass: 'text-amber-500',
    sectionClass: 'text-amber-700 dark:text-amber-400',
    rowClass: 'bg-amber-50/40 dark:bg-amber-900/10',
    dotClass: 'bg-amber-500',
    colClass: 'bg-amber-100/60 dark:bg-amber-900/30',
  },
  issue: {
    label: 'Needs Attention',
    icon: AlertCircle,
    iconClass: 'text-red-500',
    sectionClass: 'text-red-700 dark:text-red-400',
    rowClass: 'bg-red-50/40 dark:bg-red-900/10',
    dotClass: 'bg-red-500',
    colClass: 'bg-red-100/60 dark:bg-red-900/30',
  },
};

export const CategoryBreakdown: React.FC<CategoryBreakdownProps> = ({ data }) => {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const categories = data?.categories || [];
  if (categories.length === 0) return null;

  const toggle = (id: string) => setActiveCategory((prev) => (prev === id ? null : id));

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-primary">SDLC Breakdown</h2>
        <p className="text-sm text-secondary mt-1">Drill into any category for details</p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-slate-700/50 shadow-xs overflow-hidden">
        {categories.map((category, idx) => {
          const findings = category.findings || [];
          const detected = findings.filter((f) => classifyFinding(f) === 'detected');
          const gaps = findings.filter((f) => classifyFinding(f) === 'gap');
          const issues = findings.filter((f) => classifyFinding(f) === 'issue');
          const isOpen = activeCategory === category.id;
          const status: FindingGroup | null =
            issues.length > 0
              ? 'issue'
              : gaps.length > 0
                ? 'gap'
                : detected.length > 0
                  ? 'detected'
                  : null;
          const statusCfg = status ? groupConfig[status] : null;
          const StatusIcon = statusCfg?.icon;

          return (
            <div
              key={category.id}
              className={idx > 0 ? 'border-t border-slate-100 dark:border-slate-700/40' : ''}
            >
              {/* Row */}
              <button
                onClick={() => toggle(category.id)}
                className="w-full flex items-stretch hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors text-left"
              >
                {/* Status column — icon per category health */}
                <div
                  className={`w-24 shrink-0 flex items-center justify-center py-4 border-r border-slate-100 dark:border-slate-700/40 ${
                    statusCfg ? statusCfg.colClass : 'bg-slate-50 dark:bg-slate-700/20'
                  }`}
                >
                  {StatusIcon ? (
                    <StatusIcon className={`w-5 h-5 ${statusCfg!.iconClass}`} />
                  ) : (
                    <span className="text-sm font-semibold text-slate-400 dark:text-slate-500">
                      —
                    </span>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 px-4 py-3 flex flex-col justify-center gap-1.5">
                  <p className="font-semibold text-sm text-primary leading-tight">
                    {category.name}
                  </p>

                  {findings.length > 0 && (
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-secondary">
                      {detected.length > 0 && (
                        <span className="flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                          {detected.length} in place
                        </span>
                      )}
                      {severityCounts(findings).map(({ severity, count, dotClass }) => (
                        <span key={severity} className="flex items-center gap-1">
                          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dotClass}`} />
                          {count} {severity}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Chevron */}
                <div className="shrink-0 flex items-center pr-4">
                  <ChevronDown
                    className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                  />
                </div>
              </button>

              {/* Inline expanded findings */}
              {isOpen && (
                <div className="border-t-2 border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/20 px-4 py-3 space-y-4">
                  {[
                    { group: 'issue' as FindingGroup, items: issues },
                    { group: 'gap' as FindingGroup, items: gaps },
                    { group: 'detected' as FindingGroup, items: detected },
                  ].map(({ group, items }) => {
                    if (items.length === 0) return null;
                    const cfg = groupConfig[group];
                    const Icon = cfg.icon;
                    // banner color follows the worst severity inside the
                    // group — red is reserved for critical/high, so a
                    // medium-only "Needs Attention" bands yellow
                    const band =
                      group === 'detected'
                        ? { col: cfg.colClass, icon: cfg.iconClass, section: cfg.sectionClass }
                        : severityBandClasses(maxSeverity(items));
                    return (
                      <section key={group}>
                        <div className="flex items-stretch rounded-lg overflow-hidden mb-1.5">
                          <div
                            className={`w-24 shrink-0 flex items-center justify-center py-2 ${band.col}`}
                          >
                            <Icon className={`w-3.5 h-3.5 ${band.icon}`} />
                          </div>
                          <div className={`flex-1 flex items-center gap-2 px-3 py-2 ${band.col}`}>
                            <span
                              className={`text-[11px] font-semibold uppercase tracking-wide ${band.section}`}
                            >
                              {cfg.label}
                            </span>
                            <span className="text-[10px] opacity-60">({items.length})</span>
                          </div>
                        </div>
                        <div className="space-y-1.5">
                          {bySeverity(items).map((f) => {
                            const description =
                              f.description && f.description !== f.title ? f.description : '';
                            return (
                              <div
                                key={f.id}
                                className="flex items-stretch border-b border-slate-100 dark:border-slate-700/30 last:border-0"
                              >
                                <div className="w-24 shrink-0 flex items-center justify-center">
                                  <Icon
                                    className={`w-3.5 h-3.5 ${
                                      group === 'detected'
                                        ? cfg.iconClass
                                        : severityIconClass(f.severity ?? 'medium')
                                    }`}
                                  />
                                </div>
                                <div className="flex-1 min-w-0 px-3 py-2.5">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-medium text-primary leading-snug">
                                      {f.title}
                                    </span>
                                    {f.severity && group !== 'detected' && (
                                      <span
                                        className={`text-xs font-semibold px-2 py-0.5 rounded ${severityChipClass(f.severity)}`}
                                      >
                                        {f.severity.toUpperCase()}
                                      </span>
                                    )}
                                  </div>
                                  {description && (
                                    <div className="text-xs text-secondary mt-0.5 leading-relaxed">
                                      {description}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </section>
                    );
                  })}
                  {detected.length === 0 && gaps.length === 0 && issues.length === 0 && (
                    <p className="text-sm text-secondary italic">
                      Nothing to report for this category.
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CategoryBreakdown;
